import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

export type OmniSearchResultType = 'issuer' | 'portfolio' | 'claim' | 'evidence' | 'characteristic';

export interface OmniSearchResult {
  id: string;
  type: OmniSearchResultType;
  title: string;
  subtitle: string | null;
  url: string;
}

interface OmniSearchResults {
  issuers: OmniSearchResult[];
  portfolios: OmniSearchResult[];
  claims: OmniSearchResult[];
  evidence: OmniSearchResult[];
  characteristics: OmniSearchResult[];
}

const RESULTS_PER_TYPE = 4;
const DEBOUNCE_MS = 250;

const emptyResults = (): OmniSearchResults => ({
  issuers: [],
  portfolios: [],
  claims: [],
  evidence: [],
  characteristics: [],
});

export const useOmniSearch = (query: string) => {
  const [results, setResults] = useState<OmniSearchResults>(emptyResults());
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic request counter: responses from superseded keystrokes are
  // dropped so a slow early batch can't clobber fresher results or clear the
  // loading state while a newer batch is still in flight.
  const seqRef = useRef(0);

  const totalCount =
    results.issuers.length +
    results.portfolios.length +
    results.claims.length +
    results.evidence.length +
    results.characteristics.length;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      seqRef.current++;
      // Clearing on a too-short query mirrors the debounced fetch below
      // (the external system); the rule fires only since that fetch moved
      // to per-group callbacks.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResults(emptyResults());
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      const isCurrent = () => seq === seqRef.current;
      const searchTerm = `%${trimmed}%`;
      // ilike without wildcards is a case-insensitive equality; escape the
      // pattern characters so "BRK_B" can't act as a wildcard.
      const exactTicker = trimmed.replace(/[\\%_]/g, '\\$&');

      // Each result group renders as soon as its own query returns, so a fast
      // hit (a portfolio ticker like "IWV") isn't held behind the slowest
      // query — the issuer name ilike runs ~0.3–0.7s on production.
      setResults(emptyResults());
      const setGroup = <K extends keyof OmniSearchResults>(key: K, value: OmniSearchResults[K]) => {
        if (isCurrent()) setResults((prev) => ({ ...prev, [key]: value }));
      };

      // Issuer results are restricted to listable issuers — merged husks and
      // funds (ETFs, mutual funds belong to the app as portfolios) never
      // surface. `is_listable` comes from vw_issuer_listable, the same flag
      // api/list-issuers.ts filters on, so search and list agree.
      const issuersTask = (async () => {
        const [issuersRes, identifierIssuersRes, securitiesRes, exactTickerRes] = await Promise.all([
          // Issuers by name — fetch a wider candidate set so we can re-rank
          // below. PostgREST ilike doesn't promote exact / prefix matches,
          // so common queries like "apple" bury Apple Inc. behind other
          // ilike hits. Pull 4× and reorder client-side before slicing.
          //
          // IMPORTANT: name and LEI/CIK are deliberately SEPARATE queries.
          // A single .or() spanning issuer_name (base table) and
          // issuer_lei/issuer_cik (an aggregated identifiers subquery in
          // the view) defeats predicate pushdown and forces a 24k × 5k
          // join-filter scan — 9s, past the 8s statement timeout, which
          // made the whole search render nothing. Split, each side runs in
          // single-digit milliseconds off its own index.
          supabase
            .from('vw_issuer_list_enriched')
            .select('issuer_id, issuer_name, issuer_lei, issuer_cik, sic_description')
            .eq('is_listable', true)
            .ilike('issuer_name', searchTerm)
            .limit(RESULTS_PER_TYPE * 4),

          // Issuers by LEI / CIK (pasted identifiers)
          supabase
            .from('vw_issuer_list_enriched')
            .select('issuer_id, issuer_name, issuer_lei, issuer_cik, sic_description')
            .eq('is_listable', true)
            .or(`issuer_lei.ilike.${searchTerm},issuer_cik.ilike.${searchTerm}`)
            .limit(RESULTS_PER_TYPE * 2),

          // Securities (ticker → issuer)
          supabase
            .from('security')
            .select('security_symbol, exch_code, issuer!inner(issuer_id, issuer_name)')
            .ilike('security_symbol', searchTerm)
            .eq('status', 'active')
            .limit(RESULTS_PER_TYPE * 3),

          // Exact ticker. The substring query above is unordered and "LLY"
          // also matches ALLY, SALLY, … so Eli Lilly can miss its 12 rows;
          // name matches ("Kelly", "Sally") would also fill every slot first.
          supabase
            .from('security')
            .select('security_symbol, exch_code, issuer!inner(issuer_id, issuer_name)')
            .ilike('security_symbol', exactTicker)
            .eq('status', 'active')
            .limit(RESULTS_PER_TYPE),
        ]);

        // Rank issuers: exact match → name prefix → contains → shorter name.
        // This bubbles canonical matches (e.g. "Apple Inc." for the query
        // "apple") above incidental substring hits like "PINEAPPLE ENERGY".
        const lowerQuery = trimmed.toLowerCase();
        const scoreIssuer = (name: string | null | undefined): number => {
          const n = (name || '').toLowerCase();
          if (n === lowerQuery) return 0;
          if (n.startsWith(lowerQuery + ' ') || n === lowerQuery + '.') return 1;
          if (n.startsWith(lowerQuery)) return 2;
          // Word-boundary match (e.g. "apple" in "Apple Hospitality REIT").
          const wordBoundary = new RegExp(`(^|\\s)${lowerQuery.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}`, 'i');
          if (wordBoundary.test(n)) return 3;
          return 4;
        };
        // Name matches ranked first, then identifier (LEI/CIK) matches.
        const identifierRows = (identifierIssuersRes.data || []).filter(
          (r) => !(issuersRes.data || []).some((n) => n.issuer_id === r.issuer_id)
        );
        const issuerResults: OmniSearchResult[] = [
          ...(issuersRes.data || [])
            .slice()
            .sort((a, b) => {
              const scoreDiff = scoreIssuer(a.issuer_name) - scoreIssuer(b.issuer_name);
              if (scoreDiff !== 0) return scoreDiff;
              return (a.issuer_name?.length ?? 0) - (b.issuer_name?.length ?? 0);
            }),
          ...identifierRows,
        ]
          .slice(0, RESULTS_PER_TYPE)
          .map((r) => ({
            id: r.issuer_id,
            type: 'issuer' as const,
            title: r.issuer_name,
            subtitle: r.sic_description ?? r.issuer_lei ?? null,
            url: `/issuers/${r.issuer_id}`,
          }));

        // Ticker-matched issuers, exact matches first. The security query
        // joins the base issuer table, so listability is checked below for
        // the handful of issuers it surfaces.
        const seenTickerIssuerIds = new Set<string>();
        const tickerMatchedIssuers: OmniSearchResult[] = [];
        for (const sec of [...(exactTickerRes.data || []), ...(securitiesRes.data || [])]) {
          const issuer = sec.issuer as unknown as { issuer_id: string; issuer_name: string };
          if (!issuer || seenTickerIssuerIds.has(issuer.issuer_id)) continue;
          seenTickerIssuerIds.add(issuer.issuer_id);
          tickerMatchedIssuers.push({
            id: issuer.issuer_id,
            type: 'issuer' as const,
            title: issuer.issuer_name,
            subtitle: [sec.security_symbol, sec.exch_code].filter(Boolean).join(' · '),
            url: `/issuers/${issuer.issuer_id}`,
          });
        }
        let listableTickerIssuers = tickerMatchedIssuers;
        if (tickerMatchedIssuers.length > 0) {
          const { data: listableRows } = await supabase
            .from('vw_issuer_listable')
            .select('issuer_id')
            .eq('is_listable', true)
            .in('issuer_id', tickerMatchedIssuers.map((r) => r.id));
          const listable = new Set((listableRows || []).map((r: { issuer_id: string }) => r.issuer_id));
          listableTickerIssuers = tickerMatchedIssuers.filter((r) => listable.has(r.id));
        }

        // Exact-ticker matches rank ahead of everything, then name matches,
        // then the remaining ticker substring matches.
        const exactTickerIds = new Set(
          (exactTickerRes.data || []).map((sec) => (sec.issuer as unknown as { issuer_id: string }).issuer_id)
        );
        const seenIssuerIds = new Set<string>();
        const mergedIssuers = [
          ...listableTickerIssuers.filter((r) => exactTickerIds.has(r.id)),
          ...issuerResults,
          ...listableTickerIssuers.filter((r) => !exactTickerIds.has(r.id)),
        ]
          .filter((r) => (seenIssuerIds.has(r.id) ? false : (seenIssuerIds.add(r.id), true)))
          .slice(0, RESULTS_PER_TYPE);
        setGroup('issuers', mergedIssuers);
      })();

      const portfoliosTask = supabase
        .from('vw_portfolio_summary')
        .select('portfolio_id, portfolio_name, description, fund_ticker')
        .or(`portfolio_name.ilike.${searchTerm},description.ilike.${searchTerm},fund_ticker.ilike.${searchTerm}`)
        .limit(RESULTS_PER_TYPE)
        .then(({ data }) =>
          setGroup(
            'portfolios',
            (data || []).map((r) => ({
              id: r.portfolio_id,
              type: 'portfolio' as const,
              title: r.portfolio_name ?? 'Untitled Portfolio',
              subtitle: r.fund_ticker ?? r.description ?? null,
              url: `/portfolios/${r.portfolio_id}`,
            }))
          )
        );

      // Claims (via vw_claim_summary)
      const claimsTask = supabase
        .from('vw_claim_summary')
        .select('claim_id, characteristic_name, issuer_name, claim_resolution')
        .or(`characteristic_name.ilike.${searchTerm},issuer_name.ilike.${searchTerm}`)
        .limit(RESULTS_PER_TYPE)
        .then(({ data }) =>
          setGroup(
            'claims',
            (data || []).map((r) => ({
              id: r.claim_id,
              type: 'claim' as const,
              title: r.characteristic_name ?? 'Claim',
              subtitle: r.issuer_name ?? null,
              url: `/claims/${r.claim_id}`,
            }))
          )
        );

      const evidenceTask = supabase
        .from('evidence')
        .select('evidence_id, evidence_description')
        .ilike('evidence_description', searchTerm)
        .limit(RESULTS_PER_TYPE)
        .then(({ data }) =>
          setGroup(
            'evidence',
            (data || []).map((r) => ({
              id: r.evidence_id,
              type: 'evidence' as const,
              title:
                r.evidence_description
                  ? r.evidence_description.length > 80
                    ? r.evidence_description.slice(0, 80) + '…'
                    : r.evidence_description
                  : 'Evidence',
              subtitle: null,
              url: `/evidence/${r.evidence_id}`,
            }))
          )
        );

      const characteristicsTask = supabase
        .from('characteristic')
        .select('characteristic_id, characteristic_name, characteristic_description')
        .ilike('characteristic_name', searchTerm)
        .eq('is_active', true)
        .limit(RESULTS_PER_TYPE)
        .then(({ data }) =>
          setGroup(
            'characteristics',
            (data || []).map((r) => ({
              id: r.characteristic_id,
              type: 'characteristic' as const,
              title: r.characteristic_name,
              subtitle: r.characteristic_description
                ? r.characteristic_description.length > 80
                  ? r.characteristic_description.slice(0, 80) + '…'
                  : r.characteristic_description
                : null,
              url: `/characteristics/${r.characteristic_id}`,
            }))
          )
        );

      // A failed group leaves the others standing.
      const settled = await Promise.allSettled([
        issuersTask,
        portfoliosTask,
        claimsTask,
        evidenceTask,
        characteristicsTask,
      ]);
      if (!isCurrent()) return; // superseded by a newer keystroke
      for (const r of settled) {
        if (r.status === 'rejected') console.warn('Omni search failed:', r.reason);
      }
      setLoading(false);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  return { results, loading, totalCount };
};
